import {
  CreateHealthCheckItem,
  GetAllHealthCheckItems,
  DeleteHealthCheckItem,
  UpdateHealthCheckItemName,
  CreateHealthCheckItemInput,
  HealthCheckItem,
  UpdateHealthCheckItemNameInput,
  UpdateHealthCheckItemState,
} from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Health/PathItems";
import { generateUUID, Result, TransportAbstractionBuilder, TransportContext } from "@peercolab/engine";
import { TranslationAppender } from "./translationMocks";
import { applyStateTransition, transitionState } from "../../../utils/GeneralHelpers";

export function mockHealthCheckItemInterceptors(builder: TransportAbstractionBuilder, appender: TranslationAppender) {
  const dbItems: HealthCheckItem[] = [];
  const dbItemsPublished: HealthCheckItem[] = [];

  const getItem = (ctx: string | TransportContext): Result<HealthCheckItem> => {
    const id = typeof ctx === 'string' ? ctx : ctx.getPathParameter<string>('healthCheckItemId');
    const item = dbItems.find(i => i.id === id);
    if (!item) {
      return Result.notFound('Backoffice.HealthCheckItem.NotFound', `HealthCheckItem with id ${id} not found`);
    }
    return Result.ok(item);
  };

  const saveItem = (result: Result<HealthCheckItem>, ctx: TransportContext): Result<HealthCheckItem> => {
    if (!result.success) {
      return result;
    }
    const item = result.value;
    const existingIndex = dbItems.findIndex(i => i.id === item.id);
    if (existingIndex !== -1) {
      const existingPublishedIndex = dbItemsPublished.findIndex(i => i.id === item.id);
      dbItems[existingIndex] = applyStateTransition(dbItemsPublished, existingPublishedIndex, result.meta, item, item.state, (itm, newState) => {
        itm.state = newState;
        return itm;
      });
    } else {
      item.translationKey = appender('healthCheckItems', item.name);
      dbItems.push(item);
    }
    return Result.ok(item);
  };

  return builder
    .intercept(new GetAllHealthCheckItems().handle(async (input, ctx) => {
      return Result.ok([...dbItems]);
    }))
    .intercept(new CreateHealthCheckItem().handle(async (input: CreateHealthCheckItemInput, ctx) => {
      const newItem: HealthCheckItem = {
        id: input.healthCheckItemId ?? generateUUID(),
        name: input.name,
        translationKey: '',
        state: { state: 'draft', isDirty: false }
      };
      return saveItem(Result.ok(newItem), ctx);
    }))
    .intercept(new DeleteHealthCheckItem().handle(async (input, ctx) => {
      const id = ctx.getPathParameter<string>('healthCheckItemId');
      const idx = dbItems.findIndex(i => i.id === id);
      if (idx === -1) {
        return Result.notFound('Backoffice.HealthCheckItem.NotFound', `HealthCheckItem with id ${id} not found`);
      }
      dbItems.splice(idx, 1);
      return Result.ok(undefined);
    }))
    .intercept(new UpdateHealthCheckItemName().handle(async (input: UpdateHealthCheckItemNameInput, ctx) => {
      return saveItem(getItem(ctx)
        .maybe((value, meta) => {
          value.name = input.name;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateHealthCheckItemState().handle(async (input, ctx) => {
      return saveItem(getItem(ctx)
        .maybe((value, meta) => {
          value.state = transitionState(meta, value.state, input.stateTransition);
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
}
